import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Copy, 
  Trash2, 
  Archive, 
  RotateCcw, 
  X, 
  Share2,
  Clock
} from 'lucide-react';
import { Event, EventPermissions, StatusTransitionOptions, EventActionData } from '../../types/event';
import { eventAPI } from '../../services/eventAPI';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { useAlert } from '../../contexts';

interface EventActionsProps {
  event: Event;
  onEventUpdate: (updatedEvent: Event) => void;
  onEventDelete: (eventId: string) => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
}

const EventActions: React.FC<EventActionsProps> = ({
  event,
  onEventUpdate,
  onEventDelete,
  onError,
  onSuccess
}) => {
  const { showConfirm } = useAlert();
  const [permissions, setPermissions] = useState<EventPermissions | null>(null);
  const [statusOptions, setStatusOptions] = useState<StatusTransitionOptions | null>(null);
  const [showActionModal, setShowActionModal] = useState<string | null>(null);
  const [actionData, setActionData] = useState<EventActionData>({});
  const [loading, setLoading] = useState(false);

  // Load permissions and status options
  useEffect(() => {
    const loadEventData = async () => {
      try {
        const [perms, statusOpts] = await Promise.all([
          eventAPI.getEventPermissions(event.id),
          eventAPI.getStatusTransitionOptions(event.id)
        ]);
        setPermissions(perms);
        setStatusOptions(statusOpts);
      } catch (error: any) {
        onError('Failed to load event permissions');
      }
    };

    loadEventData();
  }, [event.id, onError]);

  // Handle action execution
  const executeAction = async (action: string) => {
    if (!permissions) return;

    setLoading(true);
    try {
      let updatedEvent: Event;

      switch (action) {
        case 'cancel':
          updatedEvent = await eventAPI.cancelEvent(event.id, actionData);
          onSuccess('Event cancelled successfully');
          break;
        
        case 'reschedule':
          updatedEvent = await eventAPI.rescheduleEvent(event.id, actionData);
          // Check if notifications were sent
          const notifyGuests = actionData.notify_guests !== false; // Default to true
          const notificationMessage = notifyGuests 
            ? 'Event rescheduled successfully. WhatsApp notifications sent to all guests.'
            : 'Event rescheduled successfully.';
          onSuccess(notificationMessage);
          break;
        
        case 'archive':
          updatedEvent = await eventAPI.archiveEvent(event.id, actionData);
          onSuccess('Event archived successfully');
          break;
        
        case 'reactivate':
          updatedEvent = await eventAPI.reactivateEvent(event.id, actionData);
          onSuccess('Event reactivated successfully');
          break;
        
        case 'duplicate':
          updatedEvent = await eventAPI.duplicateEvent(
            event.id, 
            actionData.reason, // Using reason field for new title
            actionData.new_date
          );
          onSuccess('Event duplicated successfully');
          break;
        
        default:
          throw new Error('Unknown action');
      }

      onEventUpdate(updatedEvent);
      setShowActionModal(null);
      setActionData({});
    } catch (error: any) {
      onError(error.response?.data?.message || `Failed to ${action} event`);
    } finally {
      setLoading(false);
    }
  };

  // Handle invitation link sharing
  const handleShareInvitation = async () => {
    try {
      const linkData = await eventAPI.getInvitationLinkData(event.id);
      const fullUrl = `${window.location.origin}${linkData.invitation_url}`;
      
      if (navigator.share) {
        await navigator.share({
          title: linkData.email_subject,
          text: linkData.share_text,
          url: fullUrl
        });
      } else {
        await navigator.clipboard.writeText(fullUrl);
        onSuccess('Invitation link copied to clipboard');
      }
    } catch (error: any) {
      onError('Failed to share invitation link');
    }
  };

  // Handle delete confirmation
  const handleDelete = async () => {
    await showConfirm(
      `Are you sure you want to delete "${event.title}"? This action cannot be undone.`,
      () => onEventDelete(event.id),
      undefined,
      'warning',
      'Delete Event'
    );
  };

  // Render action modal
  const renderActionModal = () => {
    if (!showActionModal) return null;

    const actionConfig = {
      cancel: {
        title: 'Cancel Event',
        description: 'This will cancel the event and notify all guests.',
        icon: X,
        color: 'text-red-600',
        fields: ['reason', 'notify_guests']
      },
      reschedule: {
        title: 'Reschedule Event',
        description: 'Change the date, time, or location of the event.',
        icon: Calendar,
        color: 'text-yellow-600',
        fields: ['new_date', 'new_time', 'new_location', 'reason', 'notify_guests']
      },
      archive: {
        title: 'Archive Event',
        description: 'Move the event to archived status.',
        icon: Archive,
        color: 'text-gray-600',
        fields: ['reason', 'notes']
      },
      reactivate: {
        title: 'Reactivate Event',
        description: 'Restore the event to active status.',
        icon: RotateCcw,
        color: 'text-green-600',
        fields: ['reason', 'notify_guests']
      },
      duplicate: {
        title: 'Duplicate Event',
        description: 'Create a copy of this event.',
        icon: Copy,
        color: 'text-blue-600',
        fields: ['reason', 'new_date'] // reason used as new title
      }
    };

    const config = actionConfig[showActionModal as keyof typeof actionConfig];
    const Icon = config.icon;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <Icon className={`h-6 w-6 mr-3 ${config.color}`} />
              <h3 className="text-lg font-semibold text-gray-900">{config.title}</h3>
            </div>
            
            <p className="text-gray-600 mb-6">{config.description}</p>
            
            <div className="space-y-4">
              {config.fields.includes('reason') && (
                <Input
                  label={showActionModal === 'duplicate' ? 'New Event Title' : 'Reason'}
                  placeholder={showActionModal === 'duplicate' ? 'Enter new event title' : 'Enter reason for this action'}
                  value={actionData.reason || ''}
                  onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
                />
              )}
              
              {config.fields.includes('new_date') && (
                <Input
                  type="date"
                  label="New Date"
                  value={actionData.new_date || ''}
                  onChange={(e) => setActionData({ ...actionData, new_date: e.target.value })}
                />
              )}
              
              {config.fields.includes('new_time') && (
                <Input
                  type="time"
                  label="New Time"
                  value={actionData.new_time || ''}
                  onChange={(e) => setActionData({ ...actionData, new_time: e.target.value })}
                />
              )}
              
              {config.fields.includes('new_location') && (
                <Input
                  label="New Location"
                  placeholder="Enter new location"
                  value={actionData.new_location || ''}
                  onChange={(e) => setActionData({ ...actionData, new_location: e.target.value })}
                />
              )}
              
              {config.fields.includes('notes') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    rows={3}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm"
                    placeholder="Enter any additional notes..."
                    value={actionData.notes || ''}
                    onChange={(e) => setActionData({ ...actionData, notes: e.target.value })}
                  />
                </div>
              )}
              
              {config.fields.includes('notify_guests') && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-mirsal-green focus:ring-mirsal-green"
                    checked={actionData.notify_guests !== false}
                    onChange={(e) => setActionData({ ...actionData, notify_guests: e.target.checked })}
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Notify guests about this change
                  </span>
                </label>
              )}
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowActionModal(null);
                setActionData({});
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => executeAction(showActionModal)}
              loading={loading}
              className={`${config.color.replace('text-', 'bg-').replace('-600', '-600')} hover:${config.color.replace('text-', 'bg-').replace('-600', '-700')} text-white`}
            >
              {config.title}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (!permissions || !statusOptions) {
    return (
      <div className="flex items-center justify-center p-4">
        <Clock className="h-4 w-4 animate-spin mr-2" />
        <span className="text-sm text-gray-500">Loading actions...</span>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {/* Share Invitation */}
        <Button
          size="sm"
          variant="outline"
          onClick={handleShareInvitation}
          leftIcon={<Share2 className="h-4 w-4" />}
          className="text-black border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
        >
          Share
        </Button>

        {/* Status Transition Actions */}
        {statusOptions.available_transitions.includes('canceled') && permissions.can_change_settings && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowActionModal('cancel')}
            leftIcon={<X className="h-4 w-4" />}
            className="text-red-700 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
          >
            Cancel
          </Button>
        )}

        {statusOptions.available_transitions.includes('rescheduled') && permissions.can_change_settings && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowActionModal('reschedule')}
            leftIcon={<Calendar className="h-4 w-4" />}
            className="text-yellow-700 border-yellow-300 hover:bg-yellow-50 dark:text-yellow-400 dark:border-yellow-600 dark:hover:bg-yellow-900/20"
          >
            Reschedule
          </Button>
        )}

        {statusOptions.available_transitions.includes('archived') && permissions.can_change_settings && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowActionModal('archive')}
            leftIcon={<Archive className="h-4 w-4" />}
            className="text-black border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
          >
            Archive
          </Button>
        )}

        {statusOptions.available_transitions.includes('active') && permissions.can_change_settings && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowActionModal('reactivate')}
            leftIcon={<RotateCcw className="h-4 w-4" />}
            className="text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-600 dark:hover:bg-green-900/20"
          >
            Reactivate
          </Button>
        )}

        {/* Duplicate */}
        {permissions.can_duplicate && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowActionModal('duplicate')}
            leftIcon={<Copy className="h-4 w-4" />}
            className="text-blue-700 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/20"
          >
            Duplicate
          </Button>
        )}

        {/* Delete */}
        {permissions.can_delete && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleDelete}
            leftIcon={<Trash2 className="h-4 w-4" />}
            className="text-red-700 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
          >
            Delete
          </Button>
        )}
      </div>

      {renderActionModal()}
    </>
  );
};

export default EventActions;
